/**
 * The book and chapter list (decision 13: book > chapters, reorderable; part 08: trash, orphan copies and unreadable
 * records are never hidden; part 11 log, table B). Every control is a native button, select or input, so it works
 * from the keyboard; chapters also move by drag and drop and by Alt+Up and Alt+Down.
 */
import { METADATA_TEXT } from '@cockatrice/layout';
import type { ChapterEntry } from '@cockatrice/storage';
import { createMemo } from 'solid-js';
import { el, each, when } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import type { Shell } from './shell.ts';
import type { BookSummary } from '../shell/workspace.ts';
import Button from '../ui/Button.ts';
import Menu from '../ui/Menu.ts';
import type { MenuItem } from '../ui/Menu.ts';
import { component } from '../ui/solid.ts';
import TextField from '../ui/TextField.ts';

/** The drag data type of a chapter being moved. */
export const CHAPTER_DRAG = 'application/x-cockatrice-chapter';

/** Puts the focus on a chapter's row in the list; null when it isn't there (*deep-fix r7*). */
export function focusChapter(id: string): true | null {
  const button = document.querySelector(`li[data-chapter="${id}"] .chapter-open`);
  if (!(button instanceof HTMLElement)) return null;
  button.focus();
  return true;
}

/** Whether `target` is a control that takes the focus now: in the page, not disabled, and it did take it. */
function took(target: Element | null): boolean {
  if (!(target instanceof HTMLElement) || Reflect.get(target, 'disabled') === true) return false;
  target.focus();
  return document.activeElement === target;
}

/** What the focus may land on inside an open dialog, first first. */
const DIALOG_TARGETS = ['.new-chapter', '.drawer-close', 'button, select, input, a[href], [tabindex="-1"]'];

/** Part 11d, R5; part 11e, SL1: on the home page and the settings, what may take the focus, first first. */
const PAGE_FALLBACKS = { format: ['#format-title'], home: ['.continue-writing', '#home-title', '#home-books'], settings: ['#settings-title'], sharing: ['#sharing-title'], shared: ['#shared-title'], reading: ['#read-title'] } as const;

/** Outside any dialog, after the text: what else may take the focus, first first (the text hidden by the preview: Y9). */
const PAGE_TARGETS = ['.new-chapter', '.wr-tabs [role="radio"][aria-checked="true"]', '.preview-bar [role="radio"][aria-checked="true"]'];

/**
 * Where the focus goes once the control that had it went with what it removed (*deep-fix r7*; part 11c, deep-fix r3:
 * every place is tried and checked, since the text may be hidden or behind a modal). The topmost open dialog keeps it:
 * the drawer on New chapter (its close button while that is off), any other on its first control. Else the open
 * chapter's text; else New chapter, the toolbar's switch to the text, the preview's page size.
 */
export function focusAfterRemoval(shell: Shell): void {
  const top = [...document.querySelectorAll('dialog[open]')].at(-1);
  if (top !== undefined) {
    for (const selector of DIALOG_TARGETS) {
      for (const each of top.querySelectorAll(selector)) if (took(each)) return;
    }
    return;
  }
  const route = shell.ui.route();
  if (route !== 'write') {
    for (const selector of PAGE_FALLBACKS[route]) if (took(document.querySelector(selector))) return;
    return;
  }
  if (shell.workspace.chapterId() !== null) {
    shell.focusEditor();
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
  }
  for (const selector of PAGE_TARGETS) if (took(document.querySelector(selector))) return;
}

/** A chapter's title as shown: its own, or the untitled wording. */
export function shownTitle(shell: Shell, entry: { readonly title: string }): string {
  return entry.title === '' ? shell.t('chapter.untitled') : entry.title;
}

/** A book's name as shown: its title (part 05 refuses an empty one), or what it is when its details can't be read. */
export function shownBook(shell: Shell, book: BookSummary): string {
  return book.metadata?.title ?? shell.t('book.unreadable');
}

/** Where `moving` lands when dropped on `target` (B5): before it when moving up, after it when moving down. */
export function dropBefore(chapters: readonly ChapterEntry[], moving: string, target: string): string | null | undefined {
  const from = chapters.findIndex((entry) => entry.id === moving);
  const to = chapters.findIndex((entry) => entry.id === target);
  if (from < 0 || to < 0 || from === to) return undefined;
  return from > to ? target : (chapters[to + 1]?.id ?? null);
}

/** One of a chapter's own actions, an item of its ⋯ menu: its hook, its words, when it is off, what it does. */
function action(hook: string, label: string, off: () => boolean, run: () => void, danger = false): MenuItem {
  return {
    class: `action ${hook}`,
    label,
    danger,
    get disabled() {
      return off();
    },
    onSelect: run,
  };
}

/**
 * One chapter of the list (B4, B5, C1, C4), by ID: the list is keyed by ID, so a chapter's row (and the keyboard focus
 * in it) stays when the listing is read again or the chapter moves.
 */
export function ChapterItem(shell: Shell, id: string, index: () => number, chapters: () => readonly ChapterEntry[]): Child {
  const { t, workspace, ui } = shell;
  // An item whose chapter just left the list is removed with it; until then it reads as untitled.
  const entry = createMemo(() => chapters().find((each) => each.id === id) ?? { id, title: '' });
  const title = (): string => shownTitle(shell, entry());
  const open = (): boolean => workspace.chapterId() === id;
  let button: HTMLButtonElement | undefined;
  const move = (step: 1 | -1): void => {
    const list = chapters();
    const target = list[index() + step];
    if (target === undefined) return;
    void workspace.moveChapter(id, dropBefore(list, id, target.id) ?? null).then(() => button?.focus());
  };
  const choose = (): void => {
    if (shell.mode() === 'narrow') ui.setListOpen(false);
    void workspace.openChapterById(id).then(shell.focusEditor);
  };
  // Back to the row's own button once renaming ends: the form that had the focus is gone (*deep-fix r7*).
  const doneRenaming = (): void => {
    ui.setRenaming(null);
    button?.focus();
  };
  const rename = (): Child => {
    let title = entry().title;
    return el(
      'form',
      {
        class: 'chapter-rename',
        onSubmit: (event: SubmitEvent) => {
          event.preventDefault();
          void workspace.renameChapter(id, title);
          doneRenaming();
        },
        onKeyDown: (event: KeyboardEvent) => {
          if (event.key !== 'Escape') return;
          event.preventDefault();
          doneRenaming();
        },
      },
      // A21 (part 11c): the field's label is in sight, not only told to assistive technology (the library's TextField).
      component(TextField, {
        id: `rename-${id}`,
        label: t('chapter.titleLabel'),
        fieldClass: 'chapter-rename__field',
        size: 'sm',
        name: 'title',
        defaultValue: title,
        maxLength: METADATA_TEXT.title.max,
        // Part 36a, RO (deep-fix r2): a rename open as writing stops can't be saved, so it can't be typed in either.
        get readOnly() {
          return !workspace.writable();
        },
        onChange: (value: string) => {
          title = value;
        },
        ref: (input: HTMLInputElement) => {
          queueMicrotask(() => {
            input.select();
          });
        },
      }),
      component(Button, {
        type: 'submit',
        variant: 'primary',
        size: 'sm',
        get disabled() {
          return !workspace.writable();
        },
        children: t('chapter.save'),
      }),
      component(Button, { variant: 'ghost', size: 'sm', onClick: () => { doneRenaming(); }, children: t('chapter.cancel') }),
    );
  };
  const show = (): Child =>
    el(
      'div',
      { class: 'chapter-row' },
      el(
        'button',
        {
          type: 'button',
          class: 'chapter-open',
          ref: (element: HTMLButtonElement) => {
            button = element;
          },
          'aria-current': () => (open() ? 'page' : undefined),
          onClick: () => {
            choose();
          },
          onKeyDown: (event: KeyboardEvent) => {
            if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
            event.preventDefault();
            move(event.key === 'ArrowUp' ? -1 : 1);
          },
        },
        title,
      ),
      when(
        () => open() && workspace.status() !== 'saved' && workspace.status() !== null,
        () => el('span', { class: 'unsaved-mark', role: 'img', 'aria-label': t('chapter.unsaved') }),
      ),
      // SPEC §4, PLAN §4: the chapter's own actions in one ⋯ menu (the library's), named for the chapter. Choosing one
      // closes the menu with the focus on its button first; each action then puts the focus where it did as a button:
      // the title's field, the row's title, or where the trash leaves it.
      component(Menu, {
        class: 'chapter-menu',
        iconOnly: true,
        get label() {
          return t('chapter.more', { title: title() });
        },
        items: [
          action('rename', t('chapter.rename'), () => !workspace.writable(), () => ui.setRenaming(id)),
          action('up', t('chapter.moveUp'), () => index() === 0 || !workspace.writable(), () => { move(-1); }),
          action('down', t('chapter.moveDown'), () => index() === chapters().length - 1 || !workspace.writable(), () => { move(1); }),
          action('trash', t('chapter.trash'), () => !workspace.writable(), () => void workspace.trashChapter(id).then(() => { focusAfterRemoval(shell); }), true),
        ],
      }),
    );
  return el(
    'li',
    {
      'data-chapter': id,
      class: () => (open() ? 'chapter current' : 'chapter'),
      // Part 36a, RO: nothing moves while the books are read-only.
      draggable: () => workspace.writable(),
      onDragStart: (event: DragEvent) => {
        event.dataTransfer?.setData(CHAPTER_DRAG, id);
      },
      onDragOver: (event: DragEvent) => {
        if (event.dataTransfer?.types.includes(CHAPTER_DRAG) === true) event.preventDefault();
      },
      onDrop: (event: DragEvent) => {
        const moving = event.dataTransfer?.getData(CHAPTER_DRAG) ?? '';
        const before = dropBefore(chapters(), moving, id);
        if (before === undefined) return;
        event.preventDefault();
        void workspace.moveChapter(moving, before);
      },
    },
    when(() => ui.renaming() === id, rename, show),
  );
}

/** A small action of the trash or the attention list (the library's Button): its words, when it is off, what it does. */
function small(label: string, off: () => boolean, run: () => void): Child {
  return component(Button, {
    size: 'sm',
    get disabled() {
      return off();
    },
    onClick: run,
    children: label,
  });
}

/** When a chapter in the trash was deleted; 0 for one that isn't in the trash (none the listing gives). */
export function deletedAt(entry: ChapterEntry): number {
  return entry.trashed?.at ?? 0;
}

/** The trash (B6–B8): each chapter with where and when it was deleted, restore, and delete forever. */
function Trash(shell: Shell): Child {
  const { t, workspace, ui, locale } = shell;
  const trash = (): readonly ChapterEntry[] => workspace.listing()?.trash ?? [];
  const dates = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
  return when(
    () => trash().length > 0,
    () =>
      el(
        'details',
        { class: 'trash' },
        el('summary', {}, () => t('trash.heading', { count: trash().length })),
        el(
          'ul',
          {},
          each(trash, (entry) => {
            const title = shownTitle(shell, entry);
            const date = dates.format(deletedAt(entry));
            return el(
              'li',
              {},
              el('span', { class: 'trash-title' }, title),
              el('span', { class: 'trash-when' }, () =>
                entry.trashed?.device === workspace.deviceId() ? t('trash.deletedHere', { date }) : t('trash.deletedElsewhere', { date }),
              ),
              small(t('trash.restore', { title }), () => !workspace.writable(), () => void workspace.restoreChapter(entry.id).then(() => {
                if (focusChapter(entry.id) === null) focusAfterRemoval(shell);
              })),
              small(t('trash.deleteForever', { title }), () => !workspace.writable(), () => ui.setDialog({ kind: 'delete', chapterId: entry.id, title })),
            );
          }),
        ),
        small(t('trash.empty'), () => !workspace.writable(), () => ui.setDialog({ kind: 'empty-trash', count: trash().length })),
      ),
  );
}

/**
 * What needs the writer (B9, part 08): typing kept as conflict copies, of chapters here or deleted (*deep-fix r1*), and
 * records that can't be read.
 */
function Attention(shell: Shell): Child {
  const { t, workspace, locale } = shell;
  const copies = workspace.copies;
  const invalid = () => workspace.listing()?.invalid.length ?? 0;
  const dates = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
  return when(
    () => copies().length > 0 || invalid() > 0,
    () =>
      el(
        'section',
        { class: 'attention', 'aria-labelledby': 'attention-heading' },
        el('h2', { id: 'attention-heading' }, t('attention.heading')),
        el(
          'ul',
          {},
          each(copies, ({ copy, chapter }) =>
            el(
              'li',
              {},
              el(
                'span',
                {},
                chapter === null
                  ? t('attention.copy', { date: dates.format(copy.createdAt) })
                  : t('attention.copyOf', { title: shownTitle(shell, chapter), date: dates.format(copy.createdAt) }),
              ),
              el('q', { class: 'excerpt' }, copy.text.slice(0, 120)),
              small(t('attention.recover'), () => !workspace.writable(), () => void workspace.recoverCopy(copy.id).then(shell.focusEditor)),
              small(t('attention.discard'), () => !workspace.writable(), () => shell.ui.setDialog({ kind: 'discard-copy', copyId: copy.id })),
            ),
          ),
        ),
        when(
          () => invalid() > 0,
          () => el('p', {}, () => t('attention.invalid', { count: invalid() })),
        ),
      ),
  );
}

/** The whole list: book picker, chapters, new chapter, trash, attention (table B). */
export function ChapterList(shell: Shell, foot: Child = null): Child {
  const { t, workspace } = shell;
  const chapters = createMemo(() => workspace.listing()?.chapters ?? []);
  const ids = createMemo(() => chapters().map((entry) => entry.id));
  return el(
    'nav',
    { class: 'chapter-list', 'aria-label': t('nav.label') },
    el('p', { class: 'sync-status' }, t('status.local')),
    el('ol', { class: 'chapters' }, each(ids, (id, index) => ChapterItem(shell, id, index, chapters))),
    when(
      () => chapters().length === 0,
      () => el('p', { class: 'empty' }, t('chapters.empty')),
    ),
    component(Button, {
      class: 'new-chapter',
      iconStart: 'plus',
      fullWidth: true,
      get disabled() {
        return !workspace.writable();
      },
      onClick: () => void workspace.createChapter().then(shell.focusEditor),
      children: t('chapters.new'),
    }),
    Trash(shell),
    Attention(shell),
    // Part 11f: the rail's foot, inside the list's landmark.
    foot,
  );
}
